import { useState } from 'react';
import type { RegentsOpenQuestion } from '../data/geometryRegentsNY';

interface OpenEndedQuestionProps {
  question: RegentsOpenQuestion;
  qNumber: number;
  submitted: boolean;
}

export default function OpenEndedQuestion({ question, qNumber, submitted }: OpenEndedQuestionProps) {
  const [answer, setAnswer] = useState('');
  const [showSolution, setShowSolution] = useState(false);
  const [showRubric, setShowRubric] = useState(false);
  const [showMistakes, setShowMistakes] = useState(false);

  const partColors: Record<string, string> = {
    II: 'from-teal-500 to-emerald-600',
    III: 'from-amber-500 to-orange-500',
    IV: 'from-rose-500 to-pink-600',
  };

  const partBg: Record<string, string> = {
    II: 'bg-teal-50 border-teal-200',
    III: 'bg-amber-50 border-amber-200',
    IV: 'bg-rose-50 border-rose-200',
  };

  const partText: Record<string, string> = {
    II: 'text-teal-700',
    III: 'text-amber-700',
    IV: 'text-rose-700',
  };

  const partLabel: Record<string, string> = {
    II: 'Part II — 2 pts',
    III: 'Part III — 4 pts',
    IV: 'Part IV — 6 pts',
  };

  return (
    <div className={`bg-white rounded-2xl shadow-md border-2 ${submitted ? 'border-emerald-200' : 'border-gray-100'} overflow-hidden`}>
      {/* Question Header */}
      <div className={`px-6 py-4 ${partBg[question.part]} border-b flex items-start gap-4`}>
        <div className={`w-9 h-9 flex items-center justify-center rounded-full text-sm font-bold flex-shrink-0 bg-gradient-to-br ${partColors[question.part]} text-white`}>
          {qNumber}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full bg-white ${partText[question.part]} border whitespace-nowrap`}>
              {partLabel[question.part]}
            </span>
            <span className="text-xs font-semibold text-gray-500 bg-white px-2 py-0.5 rounded-full border whitespace-nowrap">
              {question.topic}
            </span>
            <span className="text-xs text-gray-400 whitespace-nowrap">{question.standard}</span>
          </div>
          <p className="text-gray-900 font-semibold leading-relaxed text-sm whitespace-pre-line">{question.question}</p>
        </div>
      </div>

      <div className="px-6 py-5 space-y-4">
        {/* Diagram */}
        {question.diagram && (
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <i className="ri-image-line text-gray-500 text-sm"></i>
              <span className="text-xs font-bold text-gray-600 uppercase tracking-wide">Diagram</span>
            </div>
            <pre className="text-xs text-gray-700 font-mono leading-relaxed overflow-x-auto whitespace-pre">{question.diagram}</pre>
          </div>
        )}

        {/* Hint */}
        {question.hint && (
          <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
            <i className="ri-lightbulb-fill text-amber-500 text-base flex-shrink-0 mt-0.5"></i>
            <p className="text-amber-800 text-sm leading-relaxed"><strong>Hint:</strong> {question.hint}</p>
          </div>
        )}

        {/* Answer Area */}
        <div>
          <label className="block text-sm font-bold text-gray-700 mb-2">
            <i className="ri-edit-line mr-1 text-gray-500"></i>Your Work &amp; Answer:
          </label>
          <textarea
            value={answer}
            onChange={e => setAnswer(e.target.value)}
            placeholder="Show all your work here. Write each step clearly. State any theorems or formulas you use..."
            rows={question.part === 'IV' ? 10 : 6}
            className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-teal-400 resize-y font-mono leading-relaxed placeholder-gray-400"
          />
          <p className="text-xs text-gray-400 mt-1">
            <i className="ri-information-line mr-1"></i>
            On the real Regents, you must show all work to receive full credit.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2 pt-1">
          <button
            onClick={() => setShowSolution(!showSolution)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
              showSolution
                ? 'bg-emerald-100 text-emerald-800'
                : 'bg-gray-100 text-gray-600 hover:bg-emerald-50 hover:text-emerald-700'
            }`}
          >
            <i className={showSolution ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
            {showSolution ? 'Hide Sample Answer' : 'Show Sample Answer'}
          </button>
          <button
            onClick={() => setShowRubric(!showRubric)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
              showRubric
                ? 'bg-teal-100 text-teal-800'
                : 'bg-gray-100 text-gray-600 hover:bg-teal-50 hover:text-teal-700'
            }`}
          >
            <i className="ri-list-check-2"></i>
            {showRubric ? 'Hide Rubric' : 'View Scoring Rubric'}
          </button>
          {question.commonMistakes && (
            <button
              onClick={() => setShowMistakes(!showMistakes)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                showMistakes
                  ? 'bg-rose-100 text-rose-800'
                  : 'bg-gray-100 text-gray-600 hover:bg-rose-50 hover:text-rose-700'
              }`}
            >
              <i className="ri-error-warning-line"></i>
              {showMistakes ? 'Hide Common Mistakes' : 'Common Mistakes'}
            </button>
          )}
        </div>

        {/* Sample Answer */}
        {showSolution && (
          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <i className="ri-star-fill text-emerald-500"></i>
              <span className="font-bold text-emerald-800 text-sm">Full Sample Answer — Step by Step</span>
            </div>
            <ol className="space-y-2">
              {question.sampleAnswer.map((step, i) => (
                step === '' ? (
                  <li key={i} className="h-2" />
                ) : (
                  <li key={i} className="flex items-start gap-3">
                    {step.startsWith('═') || step.startsWith('STEP') || step.startsWith('PROOF') ? (
                      <p className="text-emerald-900 text-sm font-bold leading-relaxed w-full">{step}</p>
                    ) : (
                      <>
                        <span className="w-6 h-6 flex items-center justify-center bg-emerald-500 text-white rounded-full text-xs font-bold flex-shrink-0 mt-0.5">
                          {i + 1}
                        </span>
                        <p className="text-gray-800 text-sm leading-relaxed font-mono">{step}</p>
                      </>
                    )}
                  </li>
                )
              ))}
            </ol>
          </div>
        )}

        {/* Rubric */}
        {showRubric && (
          <div className="bg-teal-50 border border-teal-200 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <i className="ri-list-check-2 text-teal-600"></i>
              <span className="font-bold text-teal-800 text-sm">Official Scoring Rubric ({question.points} points)</span>
            </div>
            <ul className="space-y-2">
              {question.rubric.map((rule, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="w-6 h-6 flex items-center justify-center bg-teal-500 text-white rounded-full text-xs font-bold flex-shrink-0 mt-0.5">
                    {i + 1}
                  </span>
                  <p className="text-gray-800 text-sm leading-relaxed">{rule}</p>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Common Mistakes */}
        {showMistakes && question.commonMistakes && (
          <div className="bg-rose-50 border border-rose-200 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <i className="ri-error-warning-fill text-rose-500"></i>
              <span className="font-bold text-rose-800 text-sm">Common Mistakes to Avoid</span>
            </div>
            <ul className="space-y-2">
              {question.commonMistakes.map((mistake, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="w-5 h-5 flex items-center justify-center bg-rose-500 text-white rounded-full text-xs font-bold flex-shrink-0 mt-0.5">✗</span>
                  <p className="text-gray-800 text-sm leading-relaxed">{mistake}</p>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
